import api from '@/logic/api'
import { getLegalAcceptanceCounter, redirectToLegalConfirmation } from '@/modules/auth/legalConfirmation'

export const REQUEST_ABORTED = 'aborted'

export const relaunchApi = {
  lastConnectionErrorShown: null,
  router: null,
  store: null,
  i18n: null,
  setup(store, i18n, router) {
    this.store = store
    this.i18n = i18n
    this.router = router
  },
  send(url, method, params, body, abortController = null, keepalive = false, rejectWithErrorResponse = false) {
    const legalAcceptanceAtRequest = getLegalAcceptanceCounter()
    const headers = {
      ...this.getDefaultHeaders(),
    }
    return new Promise((resolve, reject) => {
      const requestInit = {
        headers,
        method,
        // If this was set to true, the api request won't be listed in firefox for some reason,
        // this bug still not fixed and have many issues related
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1926042
        keepalive,
      }
      if (abortController) {
        requestInit.signal = abortController.signal
      }
      if (body instanceof FormData) {
        requestInit.body = body
        if ('Content-Type' in requestInit.headers) {
          delete requestInit.headers['Content-Type']
        }
      } else if (body) {
        requestInit.body = JSON.stringify(body)
      }
      fetch(relaunchApi.getURL(url, params), requestInit).then(
        (response) => {
          if (!response.ok) {
            if (response.headers.get('x-logout-user')) {
              this.store.dispatch('auth/logout')
            } else if (response.headers.get('x-tos-not-accepted')) {
              redirectToLegalConfirmation(this.router, legalAcceptanceAtRequest)
              this.getErrorMessage(response).then(reject)
            } else if (response.headers.get('x-password-not-reset')) {
              this.router.push({
                name: 'auth.forcepasswordreset',
              })
            } else if (rejectWithErrorResponse) {
              this.getErrorResponse(response).then(reject)
            } else {
              this.getErrorMessage(response).then(reject)
            }
            return false
          }

          switch (response.headers.get('content-type')) {
            case 'application/json':
              return response.json().then(resolve).catch(reject)
            default:
              resolve(response)
          }
          return true
        },
        (error) => {
          if (
            abortController
            && abortController.signal.aborted
          ) {
            reject(REQUEST_ABORTED)
            return
          }
          this.handleConnectionError()
          reject(error)
        },
      )
    })
  },
  get(url, params) {
    return this.send(url, 'GET', params)
  },
  getWithErrorDetails(url, params) {
    return this.send(url, 'GET', params, undefined, null, false, true)
  },
  post(url, body, abortController = null) {
    return this.send(url, 'POST', undefined, body, abortController)
  },
  postAndForget(url, body, abortController = null) {
    return this.send(url, 'POST', undefined, body, abortController, true)
  },
  delete(url, body) {
    return this.send(url, 'DELETE', undefined, body)
  },
  getErrorMessage(response) {
    return this.getErrorResponse(response).then((errorResponse) => errorResponse.message)
  },
  getErrorResponse(response) {
    return response.json()
      .catch(() => ({}))
      .then((res) => {
        let message = null
        if (res.errorKey) {
          if (this.i18n.t(res.errorKey) !== res.errorKey) {
            message = this.i18n.t(res.errorKey)
          }
        }
        if (!message && res.error) {
          message = res.error
        }
        if (!message && res.message) {
          message = res.message
        }
        return {
          ...res,
          message: message || this.i18n.t('errors.no_response'),
          status: response.status,
        }
      })
  },
  handleConnectionError() {
    if (window.navigator.onLine) {
      const currentDate = Date.now()
      if (
        this.lastConnectionErrorShown
        && currentDate - this.lastConnectionErrorShown < 3000
      ) {
        return false
      }
      this.lastConnectionErrorShown = currentDate
      this.store.commit('notifications/addNotification', {
        topic: this.i18n.t('notifications.error'),
        title: 'connection error',
      })
    }
    return true
  },
  getURL(path, params = null) {
    let parsedParams = ''
    if (params !== null && Object.keys(params).length > 0) {
      parsedParams += '?'
      parsedParams += Object.keys(params)
        .map(
          (k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`,
        )
        .join('&')
    }
    let domain = process.env.VUE_APP_RELAUNCH_API_URL
    if (path.indexOf('http://') === 0 || path.indexOf('https://') === 0) {
      domain = ''
    }
    return domain + path + parsedParams
  },
  getDefaultHeaders() {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-LANGUAGE': this.i18n.locale,
      ...api.getAPIVersionHeader(),
    }
    if (this.getToken()) {
      headers['X-QUIZAPP-AUTHORIZATION'] = `Bearer ${this.getToken()}`
    }
    return headers
  },
  getToken() {
    return this.store.state.auth.token
  },
}
